<script setup lang="ts">
/**
 * The agent proposing to convert one file the user already has into Word (MAR-476).
 *
 * Deliberately NOT `WorkflowCard`. A converter is not a workflow underneath any more — MAR-487 made
 * the two of them code-only tools — but the split would stand either way: that card renders a form
 * to negotiate, and this has exactly one argument. Everything it would add — field coercion,
 * value patching, a live draft document regenerated on each edit — is not merely unused here, it is
 * harmful, because materializing the draft is what would fire the vision call before this button is
 * ever pressed.
 *
 * Both names come from the app (`resolveConversionCard`), never from the model. The user is clicking
 * on a specific, real file.
 */
import { computed } from 'vue'
import { FileType2, X, Loader2, ExternalLink } from 'lucide-vue-next'
import type { ConvertDocumentRequest } from '~/types/web-chat'

const props = defineProps<{ request: ConvertDocumentRequest }>()

const emit = defineEmits<{
  'confirm-convert': []
  'cancel-convert': []
}>()

const { t } = useI18n()

/**
 * What the card converts FROM (MAR-991). The file's own extension once the document resolves; until
 * then — or for a file with none — the kind the tool reads, which is still true of any file it accepts.
 */
const sourceFormat = computed(() => props.request.sourceFormat
  || (props.request.source === 'pdf' ? 'PDF' : t('clients.hub.convertSourceImage')))

/** The finished card's way back to the document it produced — gone only if that document was. */
const resultLink = computed(() => props.request.status === 'completed' && props.request.outputItemId
  ? `/app/docs/${props.request.outputItemId}`
  : null)
</script>

<template>
  <div class="mt-3 rounded-lg border border-border bg-muted/40 p-3">
    <div class="flex items-start gap-2">
      <div class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-lighter">
        <Loader2 v-if="request.status === 'running'" class="h-3 w-3 animate-spin text-primary" />
        <FileType2 v-else class="h-3 w-3 text-primary" />
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-xs font-medium text-foreground">{{ t('clients.hub.convertTitle') }}</p>

        <p v-if="request.loading" class="mt-0.5 text-xs leading-relaxed text-muted-foreground">
          {{ t('clients.hub.convertLoading') }}
        </p>
        <template v-else>
          <!-- i18n-t so the filename can be emphasised inside the sentence without splitting it into
               fragments translators cannot reorder. -->
          <i18n-t keypath="clients.hub.convertBody" tag="p" class="mt-0.5 text-xs leading-relaxed text-muted-foreground" scope="global">
            <template #document><span class="font-medium text-foreground">{{ request.documentName }}</span></template>
          </i18n-t>
          <p class="mt-1 text-[11px] leading-relaxed text-muted-foreground">
            {{ t('clients.hub.convertFormat', { source: sourceFormat }) }}
          </p>
          <p class="mt-1 text-[11px] leading-relaxed text-muted-foreground">
            {{ t('clients.hub.convertPreserves') }}
          </p>
        </template>

        <!-- A resolve failure is shown rather than swallowed: both ids came from the model, and one
             that does not resolve means confirming would start a run that cannot succeed.
             `request.error` is localized by whoever set it (see the type) — this renders it as-is,
             which is precisely why it must never be handed a server diagnostic (MAR-491). -->
        <p v-if="request.error" class="mt-1.5 text-xs leading-relaxed text-destructive">
          {{ request.error }}
        </p>

        <div v-if="request.status === 'pending'" class="mt-2 flex flex-wrap gap-1.5">
          <Button
            type="button"
            size="sm"
            class="gap-1.5"
            :disabled="request.loading || !!request.error"
            @click="emit('confirm-convert')"
          >
            <FileType2 class="h-3 w-3" />
            {{ t('clients.hub.convertConfirm') }}
          </Button>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent-lighter hover:text-foreground"
            @click="emit('cancel-convert')"
          >
            <X class="h-3 w-3" />
            {{ t('clients.hub.convertCancel') }}
          </button>
        </div>
        <p v-else class="mt-2 text-[11px] text-muted-foreground">
          {{ request.status === 'running'
            ? t('clients.hub.convertRunning')
            : request.status === 'completed'
              ? t('clients.hub.convertCompleted')
              : request.status === 'cancelled'
                ? t('clients.hub.convertCancelled')
                : t('clients.hub.convertFailed') }}
        </p>
        <NuxtLink
          v-if="resultLink"
          :to="resultLink"
          class="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-primary underline-offset-2 hover:underline"
        >
          <ExternalLink class="h-3 w-3" />
          {{ request.outputItemName
            ? t('clients.hub.convertOpenResult', { name: request.outputItemName })
            : t('clients.hub.convertOpenResultFallback') }}
        </NuxtLink>
      </div>
    </div>
  </div>
</template>
